import { useEffect } from "react";

// ── Share icon (iOS-style: box with upward arrow) ────────────────────
export const ShareIcon = ({ size = 22, color = "currentColor", strokeWidth = 2.2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="12" y1="14" x2="12" y2="3.5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    <polyline points="8.5,7.5 12,3.5 15.5,7.5" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 10H5C4.45 10 4 10.45 4 11V19C4 19.55 4.45 20 5 20H19C19.55 20 20 19.55 20 19V11C20 10.45 19.55 10 19 10H16" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" fill="none" />
  </svg>
);

// ── Design tokens ────────────────────────────────────────────────────
const GOLD = '#FFB700';
const BG   = '#0F0F2A';
const BG2  = '#1A1A3E';
const BG3  = '#0A0A1F';
const TEXT_DIM   = 'rgba(255,255,255,0.55)';
const TEXT_MUTED = 'rgba(255,255,255,0.35)';

const PATH_CONFIG = {
  puzzle: {
    key: 'puzzle', accent: '#00C2A8',
    name: 'PUZZLE PATH', mission: 'OPERATION RED SKY',
    tagline: 'Signal intercepted. Threat neutralised.',
    quote: '"The transmission has been decoded. Threat neutralised. Operation Red Sky — success."',
  },
  action: {
    key: 'action', accent: '#FF6B35',
    name: 'ACTION SPRINT', mission: 'VELOCITY PROTOCOL',
    quote: '"The structure is failing. You had one chance. Every second was a life."',
  },
  gamer: {
    key: 'gamer', accent: '#9B6DFF',
    name: "HERO'S JOURNEY", mission: "DRAGON'S KEEP",
    quote: '"Only a fully equipped hero can face what awaits. Your kingdom\'s fate was in your hands."',
  },
};

const EMOJI_MAP = {
  puzzle:'🧩', bolt:'⚡', sword:'⚔️', star:'⭐', clock:'⏱️',
  squad:'👥', shield:'🛡️', signal:'📡', crown:'👑',
  lock:'🔒', x:'❌', scroll:'📜', cloak:'🥷', wings:'🪽',
  surge:'💥', castle:'🏰',
};
const Icon = ({ kind, size = 22 }) => (
  <span aria-hidden style={{
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:size, height:size, fontSize:Math.round(size*0.92), lineHeight:1,
    fontFamily:'"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif',
  }}>{EMOJI_MAP[kind] || '■'}</span>
);

// ── Slide primitives ─────────────────────────────────────────────────

const Scanlines = () => (
  <div aria-hidden style={{
    position:'absolute', inset:0, pointerEvents:'none',
    backgroundImage:'repeating-linear-gradient(0deg,rgba(255,255,255,0.03) 0px,rgba(255,255,255,0.03) 1px,transparent 1px,transparent 3px)',
    mixBlendMode:'overlay', zIndex:1,
  }} />
);

const VignetteGlow = ({ color }) => (
  <div aria-hidden style={{
    position:'absolute', inset:0, pointerEvents:'none',
    background:`radial-gradient(ellipse at 50% 0%,${color}22 0%,transparent 50%),radial-gradient(ellipse at 50% 100%,${color}18 0%,transparent 55%)`,
    zIndex:0,
  }} />
);

const CornerMarks = ({ accent }) => {
  const m = (s) => <div style={{ position:'absolute', width:20, height:20, borderColor:accent, opacity:0.5, zIndex:3, ...s }} />;
  return (<>
    {m({ top:14, left:14, borderTop:`2px solid ${accent}`, borderLeft:`2px solid ${accent}` })}
    {m({ top:14, right:14, borderTop:`2px solid ${accent}`, borderRight:`2px solid ${accent}` })}
    {m({ bottom:14, left:14, borderBottom:`2px solid ${accent}`, borderLeft:`2px solid ${accent}` })}
    {m({ bottom:14, right:14, borderBottom:`2px solid ${accent}`, borderRight:`2px solid ${accent}` })}
  </>);
};

const SlideFrame = ({ children, accent }) => (
  <div style={{
    width:1080, height:1920, background:BG, color:'#fff',
    position:'relative', overflow:'hidden',
    fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif',
    display:'flex', flexDirection:'column',
  }}>
    <VignetteGlow color={accent} />
    <div aria-hidden style={{
      position:'absolute', inset:0,
      backgroundImage:`linear-gradient(${accent}08 1px,transparent 1px),linear-gradient(90deg,${accent}08 1px,transparent 1px)`,
      backgroundSize:'40px 40px', zIndex:0,
    }} />
    <Scanlines />
    <div style={{ position:'relative', zIndex:2, display:'flex', flexDirection:'column', height:'100%' }}>
      {children}
    </div>
    <CornerMarks accent={accent} />
  </div>
);

const Header = ({ path, badge, iconKind }) => (
  <div style={{
    position:'relative', padding:'44px 56px 56px',
    background:`linear-gradient(180deg,${path.accent} 0%,${path.accent}DD 100%)`,
    color:'#0A0A1F',
    clipPath:'polygon(0 0,100% 0,100% calc(100% - 24px),calc(100% - 40px) 100%,0 100%)',
  }}>
    <div style={{
      position:'absolute', top:20, right:56,
      fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif',
      fontSize:28, fontWeight:800, letterSpacing:'0.14em', lineHeight:1,
      padding:'8px 14px 10px', textTransform:'uppercase', whiteSpace:'nowrap',
      background:'#0A0A1F', color:path.accent, borderRadius:3,
      boxShadow:`0 0 0 2px #0A0A1F,0 0 16px ${path.accent}44`,
    }}>{path.name}</div>
    <div style={{
      position:'absolute', top:16, left:56,
      fontFamily:'"JetBrains Mono","Courier New",monospace',
      fontSize:14, fontWeight:700, letterSpacing:'0.22em', color:'#0A0A1F', opacity:0.6,
    }}>REC_{path.key.toUpperCase()}_001</div>
    <div style={{ display:'flex', alignItems:'center', gap:22, marginTop:48 }}>
      <div style={{
        width:88, height:88, borderRadius:4, flexShrink:0,
        background:'#0A0A1F', color:path.accent,
        display:'flex', alignItems:'center', justifyContent:'center',
        boxShadow:`0 0 0 2px #0A0A1F,inset 0 0 0 1px ${path.accent}66`,
      }}>
        <Icon kind={iconKind} size={48} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:64, fontWeight:800, letterSpacing:'0.02em', lineHeight:0.95, color:'#0A0A1F' }}>PURSUIT OCR</div>
        <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:28, fontWeight:700, letterSpacing:'0.03em', lineHeight:1.1, marginTop:8, color:'#0A0A1F', opacity:0.85, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{path.mission}</div>
        <div style={{ marginTop:10, fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:16, letterSpacing:'0.18em', fontWeight:700, color:'#0A0A1F', opacity:0.75, textTransform:'uppercase' }}>{badge}</div>
      </div>
    </div>
  </div>
);

const HeroStat = ({ accent, bigText, sub }) => (
  <div style={{ padding:'56px 56px 40px', textAlign:'center', position:'relative' }}>
    <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:14, letterSpacing:'0.35em', color:TEXT_MUTED, marginBottom:20 }}>// HERO METRIC</div>
    <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:140, fontWeight:800, lineHeight:0.9, letterSpacing:'0.01em', color:accent, textShadow:`0 0 40px ${accent}66,0 0 12px ${accent}AA` }}>{bigText}</div>
    <div style={{ marginTop:22, fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:28, fontWeight:400, fontStyle:'italic', color:'rgba(255,255,255,0.85)', letterSpacing:'0.02em', maxWidth:880, marginLeft:'auto', marginRight:'auto' }}>{sub}</div>
  </div>
);

const StatTile = ({ label, value, iconKind, iconColor, accent, extra }) => (
  <div style={{ background:BG2, border:`1px solid ${accent}33`, padding:'32px 28px', borderRadius:4, position:'relative', overflow:'hidden', minHeight:180 }}>
    <div style={{ position:'absolute', top:0, left:0, width:3, height:'100%', background:iconColor || accent }} />
    <div style={{ display:'flex', alignItems:'center', gap:10, fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:13, letterSpacing:'0.2em', color:TEXT_DIM, textTransform:'uppercase', marginBottom:14 }}>
      <Icon kind={iconKind} size={16} />{label}
    </div>
    <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:60, fontWeight:700, color:iconColor || '#fff', lineHeight:1, letterSpacing:'0.01em' }}>{value}</div>
    {extra && <div style={{ marginTop:10, fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:12, letterSpacing:'0.1em', color:TEXT_MUTED }}>{extra}</div>}
  </div>
);

const QRCode = ({ size = 130, seed = 7 }) => {
  const grid = 21;
  const rand = (x, y) => { const n = Math.sin(x*12.9898+y*78.233+seed*37.719)*43758.5453; return (n-Math.floor(n))>0.5; };
  const isFinder = (x,y) => (x<7&&y<7)||(x>grid-8&&y<7)||(x<7&&y>grid-8);
  const isFinderDot = (cx,cy,x,y) => { const dx=x-cx,dy=y-cy; return Math.max(Math.abs(dx),Math.abs(dy))===3||Math.max(Math.abs(dx),Math.abs(dy))<=1; };
  const cells = [];
  for (let y=0;y<grid;y++) for (let x=0;x<grid;x++) {
    let on;
    if (x<7&&y<7) on=isFinderDot(3,3,x,y);
    else if (x>grid-8&&y<7) on=isFinderDot(grid-4,3,x,y);
    else if (x<7&&y>grid-8) on=isFinderDot(3,grid-4,x,y);
    else if (isFinder(x,y)) on=false;
    else on=rand(x,y);
    if (on) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="#0A0A1F" />);
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${grid} ${grid}`} style={{ display:'block' }}>
      <rect width={grid} height={grid} fill="#fff" />{cells}
    </svg>
  );
};

const Footer = ({ accent }) => (
  <div style={{ padding:'32px 56px 32px', background:BG3, borderTop:`1px solid ${accent}44`, display:'flex', flexDirection:'column', alignItems:'center', gap:22, position:'relative', marginTop:'auto' }}>
    <div style={{ position:'absolute', top:0, left:0, height:2, width:'100%', background:`linear-gradient(90deg,transparent,${accent},transparent)`, opacity:0.6 }} />
    <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:16, fontWeight:700, letterSpacing:'0.22em', color:GOLD, background:`${GOLD}14`, border:`1.5px solid ${GOLD}77`, padding:'10px 22px', borderRadius:3, boxShadow:`0 0 18px ${GOLD}33` }}>★ 15% OFF YOUR NEXT THEME</div>
    <a href="https://pursuitocr.com" target="_blank" rel="noopener" style={{ display:'inline-flex', alignItems:'center', gap:10, fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:36, fontWeight:800, letterSpacing:'0.04em', color:'#0A0A1F', background:accent, padding:'14px 26px', borderRadius:4, lineHeight:1, textDecoration:'none', whiteSpace:'nowrap', boxShadow:`0 0 28px ${accent}77,0 0 0 1px ${accent}` }}>BOOK SESSION 2 →</a>
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10, marginTop:4 }}>
      <div style={{ padding:12, background:'#fff', borderRadius:4, border:`3px solid ${accent}`, boxShadow:`0 0 24px ${accent}66` }}>
        <QRCode size={130} seed={accent.charCodeAt(1)} />
      </div>
      <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:12, letterSpacing:'0.28em', color:accent, fontWeight:600 }}>SCAN TO REBOOK</div>
    </div>
    <div style={{ position:'absolute', top:12, left:20, fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:10, letterSpacing:'0.3em', color:TEXT_MUTED }}>PURSUIT // OCR</div>
  </div>
);

const Quote = ({ text, accent }) => (
  <div style={{ padding:'0 56px', position:'relative', width:'100%' }}>
    <div style={{ borderLeft:`3px solid ${accent}`, padding:'26px 28px', background:`${accent}10`, position:'relative', minHeight:120, display:'flex', alignItems:'center' }}>
      <div style={{ position:'absolute', top:-10, left:14, background:BG, padding:'0 10px', fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:11, letterSpacing:'0.28em', color:accent, fontWeight:600 }}>// TRANSMISSION</div>
      <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:32, fontStyle:'italic', fontWeight:300, color:'rgba(255,255,255,0.9)', lineHeight:1.3, letterSpacing:'0.01em' }}>{text}</div>
    </div>
  </div>
);

// ── Path-specific visuals ────────────────────────────────────────────

const CHECKPOINTS = ['Climbing Wall','Salmon Ladder','Pipe Traverse','Monkey Bars','Warped Wall','Ball Pit'];

const SignalMap = ({ accent, cleared }) => {
  const W=968, H=230;
  const pts = CHECKPOINTS.map((_,i) => ({ x:80+(i/5)*(W-160), y:H/2+Math.sin(i*1.2)*34 }));
  return (
    <div style={{ background:BG2, border:`1px solid ${accent}33`, padding:'18px 16px 14px', borderRadius:4 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 6px 10px', fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:10, letterSpacing:'0.2em', color:TEXT_DIM }}>
        <span>// SIGNAL ROUTE · 6 NODES</span><span style={{ color:accent }}>STATUS: LINKED</span>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H+36}`} style={{ display:'block' }}>
        <defs><linearGradient id="rl" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={accent} stopOpacity="0.3"/><stop offset="50%" stopColor={accent} stopOpacity="1"/><stop offset="100%" stopColor={accent} stopOpacity="0.3"/></linearGradient></defs>
        {pts.slice(0,-1).map((p,i) => { const p2=pts[i+1],done=i<cleared-1; return <line key={i} x1={p.x} y1={p.y} x2={p2.x} y2={p2.y} stroke={done?accent:`${accent}33`} strokeWidth={done?2:1.5} strokeDasharray={done?'0':'4 4'} />; })}
        {pts.map((p,i) => { const done=i<cleared; return (
          <g key={i}>
            {done&&<circle cx={p.x} cy={p.y} r={22} fill={accent} opacity={0.18}/>}
            <circle cx={p.x} cy={p.y} r={done?14:10} fill={done?accent:'none'} stroke={accent} strokeWidth={2}/>
            <text x={p.x} y={p.y+4} textAnchor="middle" fill={done?BG:accent} fontFamily="JetBrains Mono,monospace" fontSize="11" fontWeight="700">{i+1}</text>
            <text x={p.x} y={H+8} textAnchor="middle" fill={done?'rgba(255,255,255,0.75)':TEXT_MUTED} fontFamily="JetBrains Mono,monospace" fontSize="10" letterSpacing="0.05em">{CHECKPOINTS[i].toUpperCase()}</text>
            <text x={p.x} y={H+22} textAnchor="middle" fill={done?accent:TEXT_MUTED} fontFamily="JetBrains Mono,monospace" fontSize="8" letterSpacing="0.15em">{done?'LINKED':'OFFLINE'}</text>
          </g>
        ); })}
      </svg>
    </div>
  );
};

const ACTION_SQUAD = [
  { name:'REYES', role:'SCOUT',    iconKind:'signal' },
  { name:'MIRA',  role:'MEDIC',    iconKind:'shield' },
  { name:'TOMAS', role:'ENGINEER', iconKind:'surge'  },
  { name:'JIN',   role:'HACKER',   iconKind:'scroll' },
  { name:'DEX',   role:'PILOT',    iconKind:'wings'  },
  { name:'ATLAS', role:'COMMANDER',iconKind:'crown'  },
];

const SquadRoster = ({ accent, saved }) => (
  <div style={{ background:BG2, border:`1px solid ${accent}33`, padding:'18px 20px', borderRadius:4 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 0 14px', fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:10, letterSpacing:'0.2em', color:TEXT_DIM }}>
      <span>// SQUAD ROSTER · ALPHA TEAM</span><span style={{ color:accent }}>{saved} ALIVE · {6-saved} KIA</span>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
      {ACTION_SQUAD.map((m,i) => { const alive=i<saved; return (
        <div key={m.name} style={{ background:alive?`${accent}14`:'#2A0E12', border:`1.5px solid ${alive?accent:'#8B1A2A'}`, borderRadius:4, padding:'12px 10px', textAlign:'center', position:'relative', boxShadow:alive?`0 0 16px ${accent}33`:'none', opacity:alive?1:0.55 }}>
          {!alive&&<div style={{ position:'absolute', top:6, right:6, color:'#FF4D6D' }}><Icon kind="x" size={14}/></div>}
          <div style={{ width:54, height:54, margin:'0 auto 8px', borderRadius:'50%', background:alive?'#0A0A1F':'#1A0A10', border:`2px solid ${alive?accent:'#8B1A2A'}`, display:'flex', alignItems:'center', justifyContent:'center' }}><Icon kind={m.iconKind} size={26}/></div>
          <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:20, fontWeight:700, letterSpacing:'0.08em', color:alive?'#fff':'rgba(255,255,255,0.5)', lineHeight:1 }}>{m.name}</div>
          <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:9, letterSpacing:'0.2em', color:alive?accent:'#8B1A2A', marginTop:2 }}>{m.role}</div>
          <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:8, letterSpacing:'0.15em', color:alive?'rgba(255,255,255,0.55)':'#8B1A2A', marginTop:4 }}>{alive?'[ ALIVE ]':'[ KIA ]'}</div>
        </div>
      ); })}
    </div>
  </div>
);

const GEAR_ITEMS = [
  { name:'IRON SHIELD',     iconKind:'shield', legendary:false },
  { name:'ANCIENT SCROLL',  iconKind:'scroll', legendary:false },
  { name:'STEALTH CLOAK',   iconKind:'cloak',  legendary:false },
  { name:'WINGS OF ASCENT', iconKind:'wings',  legendary:true  },
  { name:'POWER SURGE',     iconKind:'surge',  legendary:false },
  { name:'DRAGON SWORD',    iconKind:'sword',  legendary:true  },
];

const GearGrid = ({ accent, collected }) => (
  <div style={{ background:BG2, border:`1px solid ${accent}33`, padding:'18px 20px', borderRadius:4 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 0 14px', fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:10, letterSpacing:'0.2em', color:TEXT_DIM }}>
      <span>// HERO LOADOUT · 3 × 2 INVENTORY</span><span style={{ color:accent }}>{collected} / 6 EQUIPPED</span>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
      {GEAR_ITEMS.map((g,i) => { const unlocked=i<collected; const color=g.legendary&&unlocked?GOLD:(unlocked?accent:'#2A2A4A'); return (
        <div key={g.name} style={{ background:unlocked?`${color}14`:'#0B0B1C', border:`1.5px solid ${unlocked?color:'#2A2A4A'}`, borderRadius:4, padding:'14px 10px', textAlign:'center', position:'relative', boxShadow:unlocked?`0 0 16px ${color}44,inset 0 0 12px ${color}22`:'none', minHeight:118 }}>
          {g.legendary&&unlocked&&<div style={{ position:'absolute', top:4, right:6, fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:8, letterSpacing:'0.2em', color:GOLD, fontWeight:700 }}>★ LEGENDARY</div>}
          {!unlocked&&<div style={{ position:'absolute', top:8, right:8, color:'#4A4A6E' }}><Icon kind="lock" size={12}/></div>}
          <div style={{ width:50, height:50, margin:'8px auto 10px', display:'flex', alignItems:'center', justifyContent:'center', filter:unlocked?`drop-shadow(0 0 6px ${color}AA)`:'none' }}><Icon kind={g.iconKind} size={40}/></div>
          <div style={{ fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif', fontSize:17, fontWeight:700, letterSpacing:'0.08em', color:unlocked?'#fff':'rgba(255,255,255,0.3)', lineHeight:1 }}>{g.name}</div>
          <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:8, letterSpacing:'0.2em', color:unlocked?color:'#4A4A6E', marginTop:4 }}>{unlocked?'UNLOCKED':'LOCKED'}</div>
        </div>
      ); })}
    </div>
  </div>
);

// ── Slide components ─────────────────────────────────────────────────

const PuzzleSlide = ({ xp, checkpointsCleared }) => {
  const path = PATH_CONFIG.puzzle; const accent = path.accent;
  return (
    <SlideFrame accent={accent}>
      <Header path={path} badge="MISSION COMPLETE ✓" iconKind="puzzle" />
      <HeroStat accent={accent} bigText={<><span>{checkpointsCleared}</span><span style={{ color:'rgba(255,255,255,0.35)' }}> / 6</span><div style={{ fontSize:52, fontWeight:700, marginTop:8, letterSpacing:'0.12em' }}>TRANSMISSIONS DECODED</div></>} sub={path.tagline} />
      <div style={{ padding:'12px 56px 28px' }}><SignalMap accent={accent} cleared={checkpointsCleared} /></div>
      <div style={{ padding:'0 56px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <StatTile label="XP Earned" value={xp.toLocaleString()} iconKind="star" iconColor={GOLD} accent={accent} extra="+150 path bonus" />
        <StatTile label="Checkpoints" value={`${checkpointsCleared} / 6`} iconKind="puzzle" iconColor={accent} accent={accent} />
        <StatTile label="Mission Time" value="~45 min" iconKind="clock" iconColor="#fff" accent={accent} />
        <StatTile label="Team Status" value="OPERATIONAL" iconKind="signal" iconColor="#4ADE80" accent={accent} />
      </div>
      <div style={{ marginTop:24 }}><Quote text={path.quote} accent={accent} /></div>
      <Footer accent={accent} />
    </SlideFrame>
  );
};

const ActionSlide = ({ xp, alliesSaved, bestSplit, totalTime }) => {
  const path = PATH_CONFIG.action; const accent = path.accent;
  let badge, subCopy;
  if (alliesSaved===6) { badge='ALL ALLIES RESCUED 🏅'; subCopy='Perfect rescue. Your squad is intact — legendary performance.'; }
  else if (alliesSaved>=4) { badge='RESCUE COMPLETE 🏆'; subCopy='Strong effort. Come back to rescue the rest.'; }
  else { badge='MISSION INCOMPLETE 💔'; subCopy='The mission was brutal. Return and bring everyone home.'; }
  return (
    <SlideFrame accent={accent}>
      <Header path={path} badge={badge} iconKind="bolt" />
      <HeroStat accent={accent} bigText={<><span>{alliesSaved}</span><span style={{ color:'rgba(255,255,255,0.35)' }}> / 6</span><div style={{ fontSize:52, fontWeight:700, marginTop:8, letterSpacing:'0.12em' }}>ALLIES RESCUED</div></>} sub={subCopy} />
      <div style={{ padding:'12px 56px 24px' }}><SquadRoster accent={accent} saved={alliesSaved} /></div>
      <div style={{ padding:'0 56px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <StatTile label="XP Earned" value={xp.toLocaleString()} iconKind="star" iconColor={GOLD} accent={accent} extra="+80 speed bonus" />
        <StatTile label="Squad Rescued" value={`${alliesSaved} / 6`} iconKind="squad" iconColor={accent} accent={accent} />
        <StatTile label="Fastest Split" value={bestSplit} iconKind="bolt" iconColor={accent} accent={accent} />
        <StatTile label="Total Time" value={totalTime} iconKind="clock" iconColor="#fff" accent={accent} />
      </div>
      <div style={{ marginTop:20 }}><Quote text={path.quote} accent={accent} /></div>
      <Footer accent={accent} />
    </SlideFrame>
  );
};

const GamerSlide = ({ xp, gearCollected }) => {
  const path = PATH_CONFIG.gamer; const accent = path.accent;
  const badge = gearCollected===6?'DRAGON SLAYED 🐉':'QUEST INCOMPLETE ⚔️';
  const subCopy = gearCollected===6?'Fully equipped. Champion of the Keep.':'Return to complete your loadout and face the dragon.';
  const legendaryUnlocked = GEAR_ITEMS.filter((g,i) => g.legendary&&i<gearCollected).map(g => g.name.split(' ')[0]);
  const legendaryValue = legendaryUnlocked.length===0?'NONE':legendaryUnlocked.length===1?`${legendaryUnlocked[0]} ✦`:`${legendaryUnlocked.length} / 2`;
  return (
    <SlideFrame accent={accent}>
      <Header path={path} badge={badge} iconKind="sword" />
      <HeroStat accent={accent} bigText={<><span>{gearCollected}</span><span style={{ color:'rgba(255,255,255,0.35)' }}> / 6</span><div style={{ fontSize:52, fontWeight:700, marginTop:8, letterSpacing:'0.12em' }}>GEAR PIECES COLLECTED</div></>} sub={subCopy} />
      <div style={{ padding:'8px 56px 20px' }}><GearGrid accent={accent} collected={gearCollected} /></div>
      <div style={{ padding:'0 56px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <StatTile label="XP Earned" value={xp.toLocaleString()} iconKind="star" iconColor={GOLD} accent={accent} extra="+220 hero bonus" />
        <StatTile label="Gear Collected" value={`${gearCollected} / 6`} iconKind="sword" iconColor={accent} accent={accent} />
        <StatTile label="Legendary Items" value={legendaryValue} iconKind="star" iconColor={legendaryUnlocked.length>0?GOLD:'#4A4A6E'} accent={accent} />
        <StatTile label="Hero Status" value={gearCollected===6?'CHAMPION':'INCOMPLETE'} iconKind="castle" iconColor={gearCollected===6?GOLD:'#fff'} accent={accent} />
      </div>
      <div style={{ marginTop:20 }}><Quote text={path.quote} accent={accent} /></div>
      <Footer accent={accent} />
    </SlideFrame>
  );
};

// ── RecapScreen — full screen, not a modal ───────────────────────────

export default function RecapScreen({ path, xpEarned, allyResults, gearUnlocked, splitTimes, onClose, vpWidth = 390 }) {
  const savedCount = allyResults ? allyResults.filter(Boolean).length : 0;
  const totalTimeSec = splitTimes ? splitTimes.reduce((a,b) => a+b, 0) : 0;
  const bestSplitSec = splitTimes && splitTimes.length ? Math.min(...splitTimes) : 0;
  const fmt = (s) => { const m = Math.floor(s/60); return `${m}:${String(s%60).padStart(2,'0')}`; };

  const accent = PATH_CONFIG[path.id]?.accent || path.color;

  // Responsive: cap slide at 480px wide on tablet/desktop, center it
  const slideDisplayWidth = Math.min(vpWidth, 480);
  const slideScale = slideDisplayWidth / 1080;
  const slideDisplayHeight = Math.round(1920 * slideScale);

  // Load fonts once
  useEffect(() => {
    if (!document.getElementById('recap-fonts')) {
      const link = document.createElement('link');
      link.id = 'recap-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const handleShare = async () => {
    const missionName = PATH_CONFIG[path.id]?.mission || path.narrative;
    const sharePayload = {
      title: `Pursuit OCR — ${missionName}`,
      text: getShareText(path.id, savedCount, gearUnlocked, xpEarned),
      url: 'https://pursuitocr.com',
    };
    if (navigator.share) {
      try { await navigator.share(sharePayload); } catch(e) { /* user cancelled */ }
    } else {
      // Desktop fallback: print → Save as PDF
      window.print();
    }
  };

  const renderSlide = () => {
    if (path.id === 'puzzle') return <PuzzleSlide xp={xpEarned} checkpointsCleared={6} />;
    if (path.id === 'action') return <ActionSlide xp={xpEarned} alliesSaved={savedCount} bestSplit={fmt(bestSplitSec)} totalTime={fmt(totalTimeSec)} />;
    if (path.id === 'gamer') return <GamerSlide xp={xpEarned} gearCollected={gearUnlocked ? gearUnlocked.length : 0} />;
    return null;
  };

  return (
    // position:absolute fills parent space — no fixed overlay issues on tablet/desktop
    <div style={{
      position:'absolute', inset:0, zIndex:100,
      background:'#080818',
      display:'flex', flexDirection:'column',
      overflow:'hidden',
    }}>
      {/* Top bar */}
      <div style={{
        height:52, flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'space-between',
        paddingLeft:16, paddingRight:12,
        background:'#0A0A1F',
        borderBottom:`1px solid ${accent}44`,
      }}>
        <button onClick={onClose} style={{
          background:'transparent', border:'none',
          color:'rgba(255,255,255,0.7)', cursor:'pointer',
          display:'flex', alignItems:'center', gap:8,
          fontSize:13, fontWeight:700, padding:'4px 0',
        }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div style={{ fontFamily:'"JetBrains Mono","Courier New",monospace', fontSize:10, letterSpacing:'0.2em', color:'rgba(255,255,255,0.35)' }}>
          MISSION RECAP
        </div>

        {/* Big share button */}
        <button
          onClick={handleShare}
          style={{
            display:'flex', alignItems:'center', gap:8,
            background:accent, color:'#0A0A1F',
            border:'none', borderRadius:6,
            paddingTop:8, paddingBottom:8, paddingLeft:14, paddingRight:14,
            fontFamily:'"Barlow Condensed","Segoe UI Semibold",sans-serif',
            fontSize:15, fontWeight:800, letterSpacing:'0.06em',
            cursor:'pointer', whiteSpace:'nowrap',
            boxShadow:`0 0 16px ${accent}66`,
          }}
        >
          <ShareIcon size={18} color="#0A0A1F" strokeWidth={2.5} />
          SHARE
        </button>
      </div>

      {/* Slide — scrollable, centered, clipped */}
      <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', display:'flex', justifyContent:'center', background:'#080818' }}>
        <div style={{ width:'100%', maxWidth:slideDisplayWidth, height:slideDisplayHeight, flexShrink:0, position:'relative', overflow:'hidden' }}>
          <div style={{
            position:'absolute', top:0, left:0,
            transform:`scale(${slideScale})`,
            transformOrigin:'top left',
          }}>
            {renderSlide()}
          </div>
        </div>
      </div>
    </div>
  );
}

function getShareText(pathId, savedCount, gearUnlocked, xpEarned) {
  if (pathId === 'puzzle') return `🧩 I just decoded all 6 transmissions in Operation Red Sky at Pursuit OCR! ${xpEarned} XP earned. Can you beat it?`;
  if (pathId === 'action') return `⚡ Velocity Protocol complete — I rescued ${savedCount}/6 allies and earned ${xpEarned} XP at Pursuit OCR. Think you can do better?`;
  if (pathId === 'gamer') return `🐉 Dragon's Keep: I collected ${gearUnlocked ? gearUnlocked.length : 0}/6 gear pieces and earned ${xpEarned} XP at Pursuit OCR. Your kingdom needs you.`;
  return `Check out my Pursuit OCR mission recap!`;
}
